import React from "react";
import { Section, Eyebrow, Tag } from "../components/ui";
import coverFront from "../assets/book/cover-front.svg";
import diagramCalmCore from "../assets/book/diagram-calm-core.svg";
import diagramStakeholders from "../assets/book/diagram-stakeholders.svg";
import diagramForecast from "../assets/book/diagram-forecast.svg";

const parts = [
  {
    t: "Part 0: The kit",
    d: "Before you start: who swings, who shields, and what's about to land on you.",
    n: 6,
  },
  {
    t: "Part 1: Beginnings",
    d: "The first weeks, when you still don't know where the toilets are and everyone is deciding what they think of you.",
    n: 34,
  },
  {
    t: "Part 2: Middles",
    d: "The long, muddy middle, where the novelty's gone and the steering committee hasn't.",
    n: 56,
  },
  {
    t: "Part 3: Ends",
    d: "Handing over, saying goodbye, and the email 6 months later that tells you whether any of it stuck.",
    n: 7,
  },
  {
    t: "Part 4: On the call",
    d: "Remote, hybrid, and what 2020 did to the work and to the people doing it.",
    n: 8,
  },
  {
    t: "Part 5: With the machine",
    d: "AI, agents, and what's left for the humans to do (more than the vendors say).",
    n: 36,
  },
  {
    t: "Part 6: Small organisations",
    d: "Startups, side projects and being your own sledgehammer, sometimes right up until the money runs out.",
    n: 3,
  },
];

const terms = [
  {
    t: "Sledgehammer",
    d: "The person with enough power to force a decision through.",
  },
  {
    t: "Shit umbrella",
    d: "The person who stands between the team and the backlash, so the team can keep working.",
  },
  {
    t: "Hand grenade",
    d: "If nobody will swing the hammer for you, make something so hard to ignore that the decision has to be made.",
  },
  {
    t: "HiPPO",
    d: "The most senior person in the room, whose opinion wins by default, whatever the data says. Can be your sledgehammer or your wall.",
  },
];

const maxims = [
  "The unit of delivery is the team.",
  "Building is cheap. Deciding is expensive.",
  "If it doesn't end with an artefact, it's not a workshop.",
  "Calm core, wild edges.",
  "You only get called when it's on fire.",
  "The incumbents aren't stupid. They're scared.",
  "Every handover is a photocopy of a photocopy.",
  "The speed is fifteen years old.",
];

const diagrams = [
  { src: diagramCalmCore, alt: "Diagram: Calm core, wild edges" },
  { src: diagramStakeholders, alt: "Diagram: Stakeholders by distance from the team" },
  { src: diagramForecast, alt: "Diagram: The shit forecast" },
];

const prereleaseHref =
  "mailto:steady@steadtec.com?subject=Sledgehammers%20prerelease&body=Put%20me%20on%20the%20prerelease%20list%20for%20Sledgehammers%20and%20Shit%20Umbrellas.";

const Book = () => (
  <main>
    <Section className="!pb-10 sm:!pb-14">
      <div className="grid gap-12 lg:grid-cols-[1fr_1.3fr] lg:items-center">
        <img
          src={coverFront}
          alt="Book cover: Sledgehammers and Shit Umbrellas, a field manual for digital transformation, by Simon Stead"
          className="mx-auto w-full max-w-xs rounded-lg border border-line shadow-sm lg:max-w-sm"
        />
        <div>
          <Eyebrow>Coming January 2027</Eyebrow>
          <h1 className="text-4xl font-black leading-[1.05] tracking-tight text-ink sm:text-5xl">
            Sledgehammers and Shit Umbrellas
          </h1>
          <p className="mt-3 text-xl font-light text-ink-soft">
            A field manual for digital transformation and agile software delivery in
            organisations too big to just decide.
          </p>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink-soft">
            150 lessons across seven parts, from the first week when you don't know where the
            toilets are, to the email six months after handover that tells you whether any of it
            stuck. Everything in it is a pattern I've used, not a theory I've read about:
            fifteen years inside startups, agencies, and national-scale public-sector delivery,
            written down.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href={prereleaseHref}
              className="rounded-full bg-ink px-6 py-3 text-base font-semibold text-paper transition-colors hover:bg-accent-dark"
            >
              Join the prerelease list
            </a>
          </div>
        </div>
      </div>
    </Section>

    <Section tone="panel">
      <Eyebrow>Two terms you'll need</Eyebrow>
      <h2 className="max-w-2xl text-3xl font-bold leading-tight text-ink sm:text-4xl">
        A sledgehammer forces the decision through. A shit umbrella keeps the backlash off the
        team while it happens.
      </h2>
      <div className="mt-10 grid gap-8 sm:grid-cols-2">
        {terms.map((term) => (
          <div key={term.t}>
            <h3 className="text-lg font-bold text-ink">{term.t}</h3>
            <p className="mt-2 text-ink-soft">{term.d}</p>
          </div>
        ))}
      </div>
    </Section>

    <Section>
      <Eyebrow>What's in it</Eyebrow>
      <h2 className="max-w-2xl text-3xl font-bold leading-tight text-ink sm:text-4xl">
        Seven parts, 150 lessons, 31 diagrams.
      </h2>
      <div className="mt-12 grid gap-10 sm:grid-cols-2">
        {parts.map((p) => (
          <div key={p.t}>
            <div className="flex items-baseline justify-between gap-4">
              <h3 className="text-lg font-bold text-ink">{p.t}</h3>
              <span className="shrink-0 font-mono text-xs text-ink-faint">{p.n} lessons</span>
            </div>
            <p className="mt-2 text-ink-soft">{p.d}</p>
          </div>
        ))}
      </div>
    </Section>

    <Section tone="panel">
      <Eyebrow>A taste of it</Eyebrow>
      <h2 className="max-w-2xl text-3xl font-bold leading-tight text-ink sm:text-4xl">
        The maxims it's built from
      </h2>
      <div className="mt-10 grid gap-x-10 gap-y-6 sm:grid-cols-2">
        {maxims.map((m) => (
          <p key={m} className="font-serif text-xl leading-snug text-ink">
            "{m}"
          </p>
        ))}
      </div>
    </Section>

    <Section>
      <Eyebrow>The diagrams</Eyebrow>
      <h2 className="max-w-2xl text-3xl font-bold leading-tight text-ink sm:text-4xl">
        31 diagrams, built to be used, not admired.
      </h2>
      <div className="mt-10 grid gap-6 sm:grid-cols-3">
        {diagrams.map((d) => (
          <img
            key={d.alt}
            src={d.src}
            alt={d.alt}
            loading="lazy"
            className="w-full rounded-lg border border-line bg-paper object-contain"
          />
        ))}
      </div>
    </Section>

    <Section tone="panel">
      <div className="text-center">
        <Eyebrow>
          <span className="mx-auto flex w-fit items-center gap-2">Out January 2027</span>
        </Eyebrow>
        <h2 className="mx-auto max-w-xl text-3xl font-bold leading-tight text-ink sm:text-4xl">
          Get the first two lessons and the launch price when it's out.
        </h2>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Tag>Digital edition</Tag>
          <Tag>Paperback</Tag>
          <Tag>Two free lessons on signup</Tag>
        </div>
        <a
          href={prereleaseHref}
          className="mt-8 inline-flex items-center gap-1 rounded-full bg-ink px-6 py-3 text-base font-semibold text-paper transition-colors hover:bg-accent-dark"
        >
          Join the prerelease list
        </a>
      </div>
    </Section>
  </main>
);

export default Book;
